// 付箋・テキストオブジェクトの本文に対する Markdown 記法（行単位のブロック記法のみ）の
// 表示モデルとスタイル定義。
//
// 方針:
// - 対象記法は「見出し（# 〜 ###### + 半角スペース）」「箇条書き（- / * / + + 半角スペース、
//   行頭スペース 2 個ごとに 1 段ネスト）」「引用（> + 半角スペース）」「水平線（---）」
//   「番号付きリスト（数字 + . + 半角スペース。箇条書きと同じネスト）」に加え、複数行をまとめる
//   ブロック記法として「表（GFM 基本形）」「コードブロック（バッククォート 3 個以上のフェンス）」。
// - `object.text` は生の Markdown テキストのまま保存し、整形は表示時のみ行う（op・domain・
//   サーバへの影響なし。編集モードでは生テキストをそのまま編集できる）。
// - **行の分類（記法プレフィックスと本文の分離）は domain の markdown-source-map.ts を正本とする。**
//   編集 DOM・非編集表示・PDF・部分書式（inline style run）の raw offset 対応が同じ判定を
//   参照するためで、本モジュールは互換のために再輸出しつつ、CSS / DOM に固有の整形だけを持つ。
// - DOM 表示（MarkdownTextView）・付箋フォント自動調整の計測（sticky-font-fit.ts）・
//   PDF 出力（markdown-canvas-layout.ts）・サムネイル（canvas-thumbnail.ts）の全経路が
//   本モジュールのパース結果とスタイル定数を共有し、画面と計測・出力の見た目を一致させる。
// - 表（GFM 基本形）は domain のブロック層（`MarkdownSourceMap.blocks`）で認識し、列幅は共有の
//   純粋関数（markdownTableColumnWidthsEm）で決める。DOM は `colgroup` へ、Canvas は同じ em 値を
//   px へ換算して使うため、画面と出力の列幅が一致する（セル内の改行位置の厳密一致は保証しない）。

import {
  buildMarkdownSourceMap,
  buildTextInlineDisplaySpans,
  buildTextInlineDisplaySpansForRange,
  bulletMarkerFor,
  displayOffsetInRange,
  isSameMarkdownInlineDecoration,
  markdownCalloutHasTitle,
  markdownCalloutTitleDisplayText,
  markdownHeadingLevel,
  markdownLineDisplaySegments,
  markdownOrderedSourceNumber,
  markdownRawLineText,
  markdownTableCellDisplayText,
  markdownTableColumnWidthsEm,
  parseMarkdownLineParts,
  splitRawRangeByRuns,
  tableColumnWidthsAt,
  BULLET_MARKERS,
  BULLET_MAX_LEVEL,
  NO_MARKDOWN_INLINE_DECORATION,
  ORDERED_LABEL_MAX_LEVEL,
  type MarkdownCallout,
  type MarkdownCalloutType,
  type MarkdownCodeBlock,
  type MarkdownHeadingLevel,
  type MarkdownLineParts,
  type MarkdownSourceLine,
  type MarkdownSourceMap,
  type MarkdownTable,
  type MarkdownTableAlign,
  type MarkdownTableCell,
  type MarkdownTableRow,
  type TableColumnWidths,
  type TableColumnWidthsList,
  type TextInlineDisplaySpan,
  type TextInlineRawPiece,
  type TextInlineStyle,
  type TextInlineStyleRange,
} from "@refinement-canvas/domain";
import {
  textInlineDecorationClassName,
  textInlineStyleCss,
  type TextInlineStyleCss,
} from "./text-inline-style-css";

// 行分類は domain（markdown-source-map.ts）を単一の真実として共有する。既存の import 互換の
// ため、従来この場所にあった公開 API はそのまま再輸出する。
export { bulletMarkerFor, parseMarkdownLineParts, BULLET_MARKERS, BULLET_MAX_LEVEL };
export type { MarkdownLineParts };

/** 見出しレベル（# の個数。1〜6）。 */
export type HeadingLevel = MarkdownHeadingLevel;

export type MarkdownTextLine =
  | MarkdownHeadingLine
  | MarkdownBulletLine
  | MarkdownTaskLine
  | MarkdownQuoteLine
  | MarkdownOrderedLine
  | MarkdownRuleLine
  | MarkdownPlainLine
  | MarkdownBlankLine;

export interface MarkdownHeadingLine {
  readonly kind: "heading";
  /** 見出しレベル（# の個数。1〜6）。 */
  readonly level: HeadingLevel;
  /** マーカーを除いた本文。 */
  readonly content: string;
}

export interface MarkdownBulletLine {
  readonly kind: "bullet";
  /** ネストレベル（floor(先頭半角スペース数 / 2) を BULLET_MAX_LEVEL でクランプ）。 */
  readonly level: number;
  /** マーカーを除いた本文（空文字列 = マーカーのみの行）。 */
  readonly content: string;
}

/** Markdown タスクリスト（`- [ ] 本文` / `- [x] 本文`）。 */
export interface MarkdownTaskLine {
  readonly kind: "task";
  readonly level: number;
  readonly content: string;
  readonly checked: boolean;
}

/** 引用（`> 本文`）。マーカーは表示せず、本文色の左の縦罫（CSS クラス）で表す。 */
export interface MarkdownQuoteLine {
  readonly kind: "quote";
  /** マーカーを除いた本文（空文字列 = `>` だけの行）。 */
  readonly content: string;
}

/** 番号付きリスト（`1. 本文`）。インデント・項目間隔は箇条書きと同じ値を使う。 */
export interface MarkdownOrderedLine {
  readonly kind: "ordered";
  /** ネストレベル（箇条書きと同じ規則でクランプ）。 */
  readonly level: number;
  /** マーカーを除いた本文（空文字列 = 数字だけの行）。 */
  readonly content: string;
  /**
   * 採番結果の表示用接頭辞。項番（"1." / "1-1."）のほか、項番を割り当てない第 3 階層以降では
   * 箇条書きマーカーの文字（"•"）が入る。いずれも assignOrderedListNumbers が決める。
   */
  readonly label: string;
}

/** 水平線（`---`）。表示テキストは空で、横罫は CSS クラス（DOM）または矩形（Canvas）で描く。 */
export interface MarkdownRuleLine {
  readonly kind: "rule";
  /** 行全体（`---`）。非編集表示には出さないが、編集 DOM は文字として残す。 */
  readonly content: string;
}

export interface MarkdownPlainLine {
  readonly kind: "plain";
  readonly content: string;
}

/** 空行。表示では 1 行分の高さ（基準フォントの行高）を維持する。 */
export interface MarkdownBlankLine {
  readonly kind: "blank";
}

/** 見出しレベルごとのフォント倍率（基準フォントサイズに対する em 値）。いずれも太字にする。 */
export const HEADING_FONT_SCALE: Readonly<Record<HeadingLevel, number>> = {
  1: 2,
  2: 1.5,
  3: 1.25,
  4: 1.1,
  5: 1.1,
  6: 1.1,
};

/**
 * 箇条書きのマーカー分のぶら下げ幅（em）。デザインシステム「箇条書きリスト」実装の
 * `.ds-list { padding-left: 1.5em }` に相当する。1 行目はこの幅ぶん負の textIndent でマーカーを
 * 本文の左へ出し、折り返し行は本文位置へそろえる。
 */
export const BULLET_HANGING_EM = 1.5;

/** Markdown チェックボックスの箱の一辺（em）。 */
export const TASK_BOX_EM = 1.25;
/** 箱の右端から本文までの余白（em）。箱との合計は箇条書きのぶら下げ幅と一致する。 */
export const TASK_BOX_GAP_EM = BULLET_HANGING_EM - TASK_BOX_EM;
/** Markdown チェックボックス span の共有 CSS クラス。 */
export const MARKDOWN_TASK_BOX_CLASS = "canvas-md-task-box";
/** チェック済みの箱の塗り（デザインシステム Blue-500）。 */
export const TASK_BOX_CHECKED_COLOR = "#2142c0";
/** チェックマークの色（デザインシステム on-accent）。 */
export const TASK_BOX_CHECK_COLOR = "#ffffff";

/**
 * マーカー領域の最小幅（em）。デザインシステム「箇条書きリスト」の「項番の幅」（フォントサイズに
 * よらず常に 1.5em）に相当し、ぶら下げ幅（BULLET_HANGING_EM）と同じ値を指す。
 * ぶら下げ幅は「1 行目を左へ出す量」、マーカー領域の幅は「マーカーが占有する量」という別の意味だが、
 * 1 行目の本文開始位置を折り返し行とそろえるには両者が一致していなければならないため同値にする。
 */
export const BULLET_MARKER_MIN_WIDTH_EM = BULLET_HANGING_EM;

/**
 * 項番 1 文字ぶんの見積り幅（em）。半角の数字・ハイフン・ピリオドを安全側（実描画幅より広め）で
 * 見積もる。実描画幅がこの見積りを超えた場合でも本文へ重ならないよう、DOM 側は width ではなく
 * min-width で、Canvas 側は max(見積り, 実測) で 1 行目の本文位置を決める。
 */
export const ORDERED_LABEL_CHAR_EM = 0.5;

/** 項番と本文の間に最低限確保する間隔（em）。 */
export const ORDERED_LABEL_GAP_EM = 0.25;

/**
 * 箇条書きのネスト 1 段あたりのインデント幅（em）。デザインシステム「箇条書きリスト」の
 * 階層表（第 1 階層 0、第 2 階層 2em、第 3 階層 4em）に相当し、`level * BULLET_INDENT_EM` で
 * 階層ぶんの字下げを求める。ぶら下げ幅（BULLET_HANGING_EM）とは意味が異なるため分けている。
 */
export const BULLET_INDENT_EM = 2;

/**
 * 連続する箇条書き行の間隔（em）。デザインシステム「箇条書きリスト」の「余白（アイテム間隔）」の
 * 既定 md に相当し、直前の行もリスト行のときだけ行の上に空ける（階層は見ない）。
 */
export const BULLET_ITEM_GAP_EM = 0.5;

/**
 * 引用の左罫の太さ（em）。DOM（`.canvas-md-quote` の border-left）と Canvas 出力で共有する。
 * 値の一致は markdown-text.test.ts が styles.css を読んで固定する。
 */
export const MARKDOWN_QUOTE_BAR_EM = 0.25;

/**
 * 引用の左罫と本文の間の余白（em）。DOM（`.canvas-md-quote` の padding-left）と一致させる。
 */
export const MARKDOWN_QUOTE_PADDING_EM = 0.75;

/**
 * 引用本文の開始位置（em）。DOM の border-left + padding-left の合計で、Canvas も同じ位置から
 * 本文を描き、折り返し幅もこの値ぶん狭める。
 *
 * 引用の文字色と左罫の色は定数を持たない。DOM は `.canvas-md-quote` が `currentColor` を、
 * PDF はコンテナで解決済みの本文色（付箋は背景色から反転した色、テキスト図形は文字色）を
 * そのまま使う。固定の控えめな色にすると濃い背景で引用だけが読めなくなるため。
 */
export const MARKDOWN_QUOTE_INDENT_EM = MARKDOWN_QUOTE_BAR_EM + MARKDOWN_QUOTE_PADDING_EM;

/**
 * 水平線の色。`styles.css` の明テーマ `--color-border` の解決値。
 * DOM は CSS 変数（`.canvas-md-rule` の背景グラデーション）を、Canvas 出力は本定数を使う。
 */
export const MARKDOWN_RULE_COLOR = "#d8dbe6";

/**
 * 水平線の太さ（em）。DOM の `max(1px, 0.0625em)` と同じ比率で、Canvas は
 * `max(1, baseFontPx * MARKDOWN_RULE_THICKNESS_EM)` を使う（1/16em ＝ 16px の本文でちょうど 1px）。
 * 固定 1px にすると、付箋のフォント自動調整で文字が大きくなったときに DOM と Canvas の太さが食い違う。
 */
export const MARKDOWN_RULE_THICKNESS_EM = 0.0625;

/** 引用行に付ける CSS クラス（styles.css）。本文色の左罫と左 padding を与える。 */
export const MARKDOWN_QUOTE_CLASS = "canvas-md-quote";

/** 水平線行に付ける CSS クラス（styles.css）。行の中央に横罫を背景で描く。 */
export const MARKDOWN_RULE_CLASS = "canvas-md-rule";

/** 番号付きリストの項番 span に付ける CSS クラス（styles.css）。編集対象にしない表示専用の接頭辞。 */
export const MARKDOWN_ORDERED_LABEL_CLASS = "canvas-md-ordered-label";

/**
 * 箇条書き・番号付きリストのマーカー span に付ける CSS クラス（styles.css）。
 * マーカーへ固定幅を与えてぶら下げインデントを成立させる。非編集表示・コメント本文・編集中の
 * 3 経路が同じクラスを使い、Canvas 出力も同じ幅の規則（markdownListMarkerWidthEm）を共有する。
 */
export const MARKDOWN_LIST_MARKER_CLASS = "canvas-md-list-marker";

/**
 * マーカー span の幅を渡す CSS カスタムプロパティ名。幅は行ごとに変わるため、CSS の固定値ではなく
 * インラインで渡す。styles.css の `var()` と必ず一致させる（一致は markdown-text.test.ts が固定する）。
 */
export const MARKDOWN_LIST_MARKER_WIDTH_PROPERTY = "--marker-width";

/** コードブロックの `<pre>` に付ける CSS クラス（styles.css）。等幅フォント・面・枠線・角丸を与える。 */
export const MARKDOWN_CODE_CLASS = "canvas-md-code";

/**
 * コードブロックの左右の内側余白（em）。DOM（`.canvas-md-code` の padding）と Canvas 出力で共有する。
 * 値の一致は markdown-text.test.ts が styles.css を読んで固定する。
 */
export const MARKDOWN_CODE_PAD_X_EM = 0.75;

/** コードブロックの上下の内側余白（em）。DOM の padding と一致させる。 */
export const MARKDOWN_CODE_PAD_Y_EM = 0.5;

/**
 * コードブロックの枠線の太さ（画面の CSS px）。DOM の `border: 1px` と同じ。
 * Canvas 出力は表の外枠（`TABLE_BORDER_PX`）と同じく出力スケール（pxScale）で換算した太さを
 * 枠線の描画と内側余白の両方へ使う（換算しないと拡大出力で枠が細くなり本文位置もずれる）。
 */
export const MARKDOWN_CODE_BORDER_PX = 1;

/**
 * コードブロックの面の色。`styles.css` の明テーマ `--color-surface-muted` の解決値。
 * DOM は CSS 変数を、Canvas 出力（PDF・サムネイル。常に明テーマで描く）は本定数を使う。
 */
export const MARKDOWN_CODE_BACKGROUND_COLOR = "#eef0f6";

/** コードブロックの枠線の色。`styles.css` の明テーマ `--color-border` の解決値。 */
export const MARKDOWN_CODE_BORDER_COLOR = "#d8dbe6";

/** callout のコンテナに付ける CSS クラス（styles.css）。左罫・薄い背景・角丸・余白を与える。 */
export const MARKDOWN_CALLOUT_CLASS = "canvas-md-callout";

/** callout のタイトル要素に付ける CSS クラス（styles.css）。太字と本文との間隔を与える。 */
export const MARKDOWN_CALLOUT_TITLE_CLASS = "canvas-md-callout-title";

/**
 * callout の上下の外余白（em）。DOM（`margin-block`）と Canvas 出力で共有する。
 * DOM の margin は隣接する兄弟どうしで相殺するため、Canvas も「本文の先頭・末尾では 0、
 * callout 同士が隣接する場合は合算せず 1 つぶん」という同じ規則で積む。
 */
export const CALLOUT_MARGIN_Y_EM = 0.75;

/** callout の内側の上下余白（em）。DOM の `padding` の縦成分と一致させる。 */
export const CALLOUT_PAD_Y_EM = 0.6;

/** callout の内側の左右余白（em）。DOM の `padding` の横成分と一致させる。 */
export const CALLOUT_PAD_X_EM = 0.9;

/** callout の角丸（em）。DOM の `border-radius` と一致させる（Canvas 出力は角丸にしない）。 */
export const CALLOUT_RADIUS_EM = 0.375;

/** タイトルと本文の間隔（em）。DOM の `.canvas-md-callout-title` の `margin-bottom` と一致させる。 */
export const CALLOUT_TITLE_GAP_EM = 0.9;

/**
 * callout の左罫の太さの下限（画面の CSS px）。DOM の `max(4px, 0.25em)` の固定 px 側。
 * Canvas 出力は表の外枠（`TABLE_BORDER_PX`）やコードブロックの枠線（`MARKDOWN_CODE_BORDER_PX`）と
 * 同じく、出力スケール（`pxScale`）で換算した値を使う（換算しないと、倍率が 1 以外の出力で
 * 罫の太さと本文の開始位置が画面と食い違う）。
 */
export const CALLOUT_ACCENT_MIN_PX = 4;

/** callout の左罫の太さ（em）。DOM の `max(4px, 0.25em)` の em 側。 */
export const CALLOUT_ACCENT_EM = 0.25;

/** callout の左罫と背景の色の組。 */
export interface CalloutColorPair {
  readonly accent: string;
  readonly bg: string;
}

/**
 * callout の塗り色。明るい背景用と暗い背景用の 2 組を持つ。
 * `styles.css` の `--callout-*`（および `--callout-*-on-dark`）と一致することを
 * markdown-text.test.ts が styles.css を読んで固定する。
 * 背景は半透明のため、Canvas でも図形の塗りの上に重なって画面と同じ見え方になる。
 */
export const CALLOUT_COLORS: Readonly<
  Record<"light" | "onDark", Readonly<Record<MarkdownCalloutType, CalloutColorPair>>>
> = {
  light: {
    info: { accent: "#2563eb", bg: "rgba(37, 99, 235, 0.1)" },
    warn: { accent: "#d97706", bg: "rgba(217, 119, 6, 0.12)" },
    alert: { accent: "#dc2626", bg: "rgba(220, 38, 38, 0.1)" },
    note: { accent: "rgba(128, 128, 128, 0.7)", bg: "rgba(128, 128, 128, 0.1)" },
  },
  onDark: {
    info: { accent: "#60a5fa", bg: "rgba(59, 130, 246, 0.22)" },
    warn: { accent: "#fbbf24", bg: "rgba(217, 119, 6, 0.26)" },
    alert: { accent: "#f87171", bg: "rgba(220, 38, 38, 0.26)" },
    note: { accent: "rgba(148, 163, 184, 0.85)", bg: "rgba(148, 163, 184, 0.18)" },
  },
};

/**
 * callout のコンテナに付ける className（共通クラス + type 別クラス）。
 * 非編集表示・コメント本文・計測ビルダーの 3 経路が同じ結果を使う。
 */
export function markdownCalloutClassName(type: MarkdownCalloutType): string {
  return `${MARKDOWN_CALLOUT_CLASS} ${MARKDOWN_CALLOUT_CLASS}--${type}`;
}

/**
 * callout のタイトルの表示 span 列（部分書式 + 記法の装飾 + リンク）。
 * 行全体と同じ共有関数を raw 範囲指定で呼ぶため、タイトルの中でもリンク・装飾・部分書式が
 * 行と同じ規則で働く。`collapsePipeEscape` は指定しない（タイトルは表のセルではないため、
 * `\|` は従来どおり 2 文字として表示される）。
 */
export function markdownCalloutTitleSpans(
  text: string,
  callout: MarkdownCallout,
  runs: readonly TextInlineStyleRange[] | undefined
): readonly TextInlineDisplaySpan[] {
  return buildTextInlineDisplaySpansForRange({
    end: callout.titleEnd,
    runs,
    start: callout.titleStart,
    text,
  });
}

/**
 * 表の wrapper に付ける CSS クラス（styles.css）。外枠・角丸・`overflow: hidden` を与え、
 * 表の外寸だけを覆う（`width: fit-content`）。
 */
export const MARKDOWN_TABLE_WRAP_CLASS = "canvas-md-table-wrap";

/** 表本体（`<table>`）に付ける CSS クラス（styles.css）。`table-layout: fixed` と列ヘッダーの見た目。 */
export const MARKDOWN_TABLE_CLASS = "canvas-md-table";

/**
 * 列幅つまみ（ヘッダセルの右端へ重ねる `<span>`）に付ける CSS クラス（styles.css）。
 * 計測ビルダー（`buildMarkdownTextContent`）はつまみを作らない。つまみは絶対配置で
 * 行送り・セル幅に影響しないため、計測結果は表示と一致する。
 */
export const MARKDOWN_TABLE_RESIZER_CLASS = "canvas-md-table-resizer";

/**
 * 表の外枠線の色。デザインシステム「テーブルの外枠」の Gray-300（`styles.css` の `--ds-gray-300`）。
 * DOM は CSS 変数（`--ds-table-border`）を、Canvas 出力は本定数を使う。
 */
export const MARKDOWN_TABLE_BORDER_COLOR = "#8f9198";

/** 表の列ヘッダーの背景色。デザインシステム「テーブルの列ヘッダー」（`--ds-table-header-bg`）。 */
export const MARKDOWN_TABLE_HEADER_BACKGROUND_COLOR = "#162675";

/** 表の列ヘッダーの文字色と列境界の縦線の色（白。`--ds-table-header-text`）。 */
export const MARKDOWN_TABLE_HEADER_TEXT_COLOR = "#ffffff";

/** 表本文の奇数行背景（`styles.css` の `--ds-table-body-bg` と同じ不透明な白）。 */
export const MARKDOWN_TABLE_BODY_BACKGROUND_COLOR = "#ffffff";

/** 表本文の偶数行背景（`styles.css` の `--ds-table-row-stripe` と同じ値）。 */
export const MARKDOWN_TABLE_ROW_STRIPE_COLOR = "#f4f6fb";

/** 暗い図形上の表本文偶数行背景（`styles.css` の `--ds-table-row-stripe-on-dark` と同じ値）。 */
export const MARKDOWN_TABLE_ROW_STRIPE_COLOR_ON_DARK = "#1e2128";

/**
 * 箇条書き系の行か（項目間隔の判定に使う）。番号付きリスト・チェックボックス等の
 * リスト記法を追加したらここへ足す。DOM 表示・計測ビルダー・編集 DOM・Canvas 出力の
 * すべてがこの述語を経由して間隔を決めるため、種別を直書きせずここだけを変えれば
 * 全経路の見た目が一致する。
 */
export function isMarkdownListLineKind(kind: MarkdownTextLine["kind"]): boolean {
  return kind === "bullet" || kind === "ordered" || kind === "task";
}

/**
 * 本文テキストを行単位の Markdown 行へパースする。改行コードは CRLF / LF の両方を受け付ける。
 * どの記法にも該当しない行は plain としてそのまま返すため、記法を含まない既存本文の
 * 見た目は従来と変わらない。
 * 番号付きリストの項番は行単体では決まらないため、source map の採番結果（orderedNumbers）を
 * 各行へ埋めてから返す（呼び出し側は 1 行だけを見て表示テキストを組み立てられる）。
 */
export function parseMarkdownTextLines(text: string): readonly MarkdownTextLine[] {
  const sourceMap = buildMarkdownSourceMap(text);
  return sourceMap.lines.map((line) =>
    markdownTextLineFromParts(line, { orderedLabel: sourceMap.orderedNumbers.get(line.lineIndex) })
  );
}

/**
 * 行の表示テキスト（箇条書きはマーカー付き、番号付きリストは項番付き、引用・見出し・plain は
 * 本文のみ、水平線は空）を返す。
 * DOM 表示（MarkdownTextView）と計測用ビルダー（buildMarkdownTextContent）が共有する。
 */
export function markdownLineTextFor(line: MarkdownTextLine): string {
  switch (line.kind) {
    case "heading":
    case "plain":
    case "quote":
      return line.content;
    case "bullet":
      return line.content.length > 0
        ? `${bulletMarkerFor(line.level)} ${line.content}`
        : bulletMarkerFor(line.level);
    case "task":
      return line.content;
    case "ordered":
      return line.content.length > 0 ? `${line.label} ${line.content}` : line.label;
    case "rule":
      // 横罫は CSS（DOM）または矩形（Canvas）で描くため、表示テキストは持たない。
      return "";
    case "blank":
      return "";
    default:
      return assertNever(line);
  }
}

/**
 * 行に付ける CSS クラス名（引用・水平線のみ。該当しない行は undefined）。
 * 引用の左罫・文字色・左 padding と、水平線の横罫は styles.css のクラスだけで定義し、
 * インラインスタイル（markdownLineStyleFor）には同じ値を書かない（定義の二重化を防ぐ）。
 * 非編集表示・計測ビルダー・編集 DOM・コメント本文の 4 経路が、この結果をそのまま
 * `className` / `classList` へ与える。
 */
export function markdownLineClassName(line: MarkdownTextLine): string | undefined {
  switch (line.kind) {
    case "quote":
      return MARKDOWN_QUOTE_CLASS;
    case "rule":
      return MARKDOWN_RULE_CLASS;
    default:
      return undefined;
  }
}

/**
 * 行の「画面に見える」表示テキストを返す。markdownLineTextFor の結果から、表示テキスト付き
 * リンク（`[テキスト](URL)`）の記法と、インライン装飾のマーカー（`**` / `*` / `~~` /
 * `` ` `` / エスケープの `\`）を、非編集表示（MarkdownTextView）と同じく取り除く
 * （素の URL はそのまま残す）。
 * 画面表示は表示セグメントを通して記法を縮約するため、付箋フォント自動調整の計測
 * （buildMarkdownTextContent）・Canvas 出力（layoutMarkdownTextForCanvas）は raw の記法長では
 * なくこの表示長で扱わないと、画面とフィット・PDF・サムネイルがずれる。
 *
 * 箇条書きのグリフを付けるかどうかは、従来どおり**生の本文長**で決める
 * （表示・計測・Canvas のグリフ有無を一致させるため）。
 */
export function markdownLineDisplayTextFor(line: MarkdownTextLine): string {
  switch (line.kind) {
    case "heading":
    case "plain":
    case "quote":
      return markdownContentDisplayText(line.content);
    case "bullet":
      return line.content.length > 0
        ? `${bulletMarkerFor(line.level)} ${markdownContentDisplayText(line.content)}`
        : bulletMarkerFor(line.level);
    case "task":
      return markdownContentDisplayText(line.content);
    case "ordered":
      return line.content.length > 0
        ? `${line.label} ${markdownContentDisplayText(line.content)}`
        : line.label;
    case "rule":
      return "";
    case "blank":
      return "";
    default:
      return assertNever(line);
  }
}

/**
 * リスト行のマーカー span / マーカーセグメントへ入れる文字列を返す（リスト行以外は空文字列）。
 * 行の表示テキスト（markdownLineTextFor）の接頭辞から**末尾の区切り空白を落とした**もので、
 * 箇条書きはグリフ（`•`）、番号付きリストは採番済みの接頭辞（`1-1.`。第 3 階層以降は `•`）になる。
 *
 * 空白を落とすのは、マーカーを固定幅のボックスの中で中央へ寄せるため。末尾の空白も文字幅として
 * 数えられると、グリフが左へ寄って見える。マーカーと本文の間隔はボックスの幅（マーカー領域）が作る。
 * 行全体の表示テキストの契約（markdownLineTextFor / markdownLineDisplayTextFor）は変えない
 * （変えると Canvas の折り返し計算・付箋の計測・キャレットの文字位置がすべて動く）。
 */
export function markdownListMarkerTextFor(line: MarkdownTextLine): string {
  switch (line.kind) {
    case "bullet":
      return bulletMarkerFor(line.level);
    case "ordered":
      return line.label;
    default:
      return "";
  }
}

/**
 * 行の「画面に見える」表示テキストから、リスト行の表示専用の接頭辞（`• ` / `1. `）を除いた
 * 本文だけを返す。リスト行以外は markdownLineDisplayTextFor と同じ。
 * マーカーを本文と別に描く経路（Canvas 出力・計測ビルダー）が、本文だけを折り返すために使う。
 */
export function markdownLineBodyDisplayTextFor(line: MarkdownTextLine): string {
  switch (line.kind) {
    case "bullet":
    case "ordered":
    case "task":
      return markdownContentDisplayText(line.content);
    default:
      return markdownLineDisplayTextFor(line);
  }
}

/**
 * 行のマーカー領域の幅（em）を返す。リスト行以外は 0。
 * デザインシステム「箇条書きリスト」の「項番の幅」（1.5em）を最小幅とし、番号付きリストは
 * 項番がその幅へ収まらない場合（`10.` 以上、`1-1.` などの枝番）だけ、その行のぶんを広げる。
 * DOM 3 経路（非編集表示・コメント本文・編集中）と Canvas 出力が、この 1 つの関数を通す。
 */
export function markdownListMarkerWidthEm(line: MarkdownTextLine): number {
  switch (line.kind) {
    case "bullet":
      return BULLET_MARKER_MIN_WIDTH_EM;
    case "task":
      return BULLET_HANGING_EM;
    case "ordered":
      return Math.max(
        BULLET_MARKER_MIN_WIDTH_EM,
        roundEm(line.label.length * ORDERED_LABEL_CHAR_EM + ORDERED_LABEL_GAP_EM)
      );
    default:
      return 0;
  }
}

/**
 * マーカー span へ与える className（リスト行以外は undefined）。
 * 番号付きリストは、既存の項番クラス（選択不可）と本クラス（固定幅）を併用する。
 * 非編集表示・コメント本文・計測ビルダーの 3 経路が同じ結果を使う。
 */
export function markdownListMarkerClassName(line: MarkdownTextLine): string | undefined {
  switch (line.kind) {
    case "bullet":
      return MARKDOWN_LIST_MARKER_CLASS;
    case "ordered":
      return `${MARKDOWN_LIST_MARKER_CLASS} ${MARKDOWN_ORDERED_LABEL_CLASS}`;
    default:
      return undefined;
  }
}

/** マーカー span へ与えるインラインスタイル（マーカー領域の幅）。 */
export interface MarkdownListMarkerStyle {
  /** `--marker-width` の値（`1.5em` など）。CSS の `min-width: var(--marker-width, 1.5em)` が読む。 */
  readonly [MARKDOWN_LIST_MARKER_WIDTH_PROPERTY]: string;
}

/**
 * マーカー span へ与えるインラインスタイルを返す（リスト行以外は undefined）。
 * React の `style` はカスタムプロパティを型として持たないため、受け取り側で 1 か所だけ
 * `CSSProperties` へ変換する。実 DOM を組み立てる経路（markdown-wysiwyg.ts・計測ビルダー）は
 * `style.setProperty` で与える。
 */
export function markdownListMarkerStyleFor(
  line: MarkdownTextLine
): MarkdownListMarkerStyle | undefined {
  const widthEm = markdownListMarkerWidthEm(line);
  if (widthEm <= 0) return undefined;
  return { [MARKDOWN_LIST_MARKER_WIDTH_PROPERTY]: `${widthEm}em` };
}

/**
 * 行のスタイル（CSS 値）を返す。DOM 表示と計測用ビルダーが共有する単一の真実。
 * - 見出し: フォント倍率（em）+ 太字
 * - 箇条書き・番号付きリスト: ぶら下げインデント（paddingLeft + 負の textIndent）+ 常に左寄せ
 *   （インデント構造が中央 / 右寄せと両立しないため。見出し・plain はコンテナの
 *   textAlign を継承する）。paddingLeft は「階層 × BULLET_INDENT_EM + マーカー領域の幅」で、
 *   直前の行もリスト行（isMarkdownListLineKind）なら項目間隔（marginTop）を空ける。
 *   番号付きリストは箇条書きとまったく同じ計算を使い、項番が最小幅へ収まる限り同じ値になる
 * - 引用・水平線: インラインスタイルを返さない。見た目は CSS クラス（markdownLineClassName）で与える
 * - context を省略した場合は「直前の行が無い」扱いで、項目間隔は付かない
 */
export function markdownLineStyleFor(
  line: MarkdownTextLine,
  context?: MarkdownLineStyleContext | undefined
): MarkdownLineStyle {
  switch (line.kind) {
    case "heading":
      return {
        fontSize: `${HEADING_FONT_SCALE[line.level]}em`,
        fontWeight: "bold",
      };
    case "bullet":
    case "ordered":
    case "task": {
      // ぶら下げは「行の padding-left」と「負の text-indent」の絶対値が一致して初めて成立する。
      // 可変幅（項番が長い行）では -2.25 のような値が出るため、textIndent も paddingLeft と
      // 同じ丸めを通す（通さないと浮動小数の差で 1 行目のマーカー位置がずれる）。
      const markerWidthEm = markdownListMarkerWidthEm(line);
      return {
        paddingLeft: `${roundEm(line.level * BULLET_INDENT_EM + markerWidthEm)}em`,
        textAlign: "left",
        textIndent: `${roundEm(-markerWidthEm)}em`,
        // 直前がリスト行なら項目間隔を空ける。判定は必ず述語（isMarkdownListLineKind）を経由し、
        // 番号付きリスト等を追加したときに DOM と Canvas の間隔が食い違わないようにする。
        ...(context?.previousKind !== undefined && isMarkdownListLineKind(context.previousKind)
          ? { marginTop: `${BULLET_ITEM_GAP_EM}em` }
          : {}),
      };
    }
    case "quote":
    case "rule":
    case "plain":
    case "blank":
      return {};
    default:
      return assertNever(line);
  }
}

export interface MarkdownLineStyle {
  readonly fontSize?: string | undefined;
  readonly fontWeight?: string | undefined;
  /** 連続するリスト行の項目間隔。直前の行がリスト行のときだけ設定される。 */
  readonly marginTop?: string | undefined;
  readonly paddingLeft?: string | undefined;
  readonly textAlign?: "left" | undefined;
  readonly textIndent?: string | undefined;
}

/** markdownLineStyleFor へ渡す、1 行だけでは決まらないスタイルのための前後関係。 */
export interface MarkdownLineStyleContext {
  /** 直前の行の種別。連続する箇条書きの項目間隔を決めるために使う（先頭行は undefined）。 */
  readonly previousKind?: MarkdownTextLine["kind"] | undefined;
}

/**
 * 本文（生 Markdown）から、表示（MarkdownTextView）と同じ構造の DOM を element 配下へ構築する。
 * 付箋フォント自動調整（sticky-font-fit.ts）の隠し計測要素が「表示と同じ整形構造」で
 * 計測できるようにするためのビルダー。
 *
 * 装飾（太字・斜体・等幅 + padding）は文字幅と行の折り返しを変えるため、装飾を含む行は
 * 表示側とまったく同じ解決（textInlineStyleCss / textInlineDecorationClassName）で span を作る。
 * 装飾を含まない行は、既存本文の計測結果を 1px も変えないよう**単一のテキストノード**のままにする。
 *
 * innerHTML / dangerouslySetInnerHTML は使わず createElement + textContent のみで構築し、
 * ユーザー入力テキストが HTML として解釈される余地を構造的に排除する。
 * 表示側との構造一致はパリティテスト（markdown-text-view.test.tsx）で担保する。
 *
 * なお、リンクの `<a>` 構造（左右 0.5em の margin・外部リンクアイコン・素 URL の省略表示）は
 * 本ビルダーも Canvas レイアウトも再現しない（本設計より前からの性質）。
 *
 * 表は `sourceMap.blocks` の `table` ブロックを、表示側（MarkdownTableView）と同じ
 * wrapper / table / colgroup / thead / tbody の構造で組む。列幅は `availableEm`（表の使用可能幅。
 * 呼び出し側が本文領域の幅と基準フォント px から `markdownTableAvailableEm` で求める）から
 * 共有関数で決めるため、表示と同じ em 値の `colgroup` になる。
 */
export function buildMarkdownTextContent(
  element: HTMLElement,
  text: string,
  options: {
    readonly availableEm: number;
    /**
     * 本文中の表ごとの保存済み列幅（ブロック順）。渡さないと計測だけが自動列幅になり、
     * 実表示と違う折り返しで付箋のフォントサイズが決まる。
     */
    readonly tableColumnWidths?: TableColumnWidthsList | undefined;
  }
): void {
  const ownerDocument = element.ownerDocument;
  element.replaceChildren();
  const sourceMap = buildMarkdownSourceMap(text);
  // 連続する箇条書きの項目間隔は、表示（MarkdownTextView）と同じ共有関数で決める。
  const previousKinds = markdownBlockPreviousKinds(sourceMap);
  // 保存済み列幅は本文中の表のブロック順で並ぶため、表だけを数える index で引く。
  let tableIndex = 0;
  for (const [blockIndex, block] of sourceMap.blocks.entries()) {
    if (block.kind === "table") {
      appendMeasuredTable(ownerDocument, element, text, block.table, options.availableEm, {
        savedWidths: tableColumnWidthsAt(options.tableColumnWidths, tableIndex),
      });
      tableIndex += 1;
      continue;
    }
    if (block.kind === "code") {
      appendMeasuredCodeBlock(ownerDocument, element, sourceMap, block.code);
      continue;
    }
    if (block.kind === "callout") {
      appendMeasuredCallout(ownerDocument, element, text, sourceMap, block.callout);
      continue;
    }
    appendMeasuredLine(
      ownerDocument,
      element,
      text,
      sourceMap,
      block.lineIndex,
      previousKinds[blockIndex]
    );
  }
}

/**
 * 各行の記法マーカー（`# ` / `- ` / `> ` 等）を除去し、表示テキスト付きリンクは表示テキストへ縮約し、
 * インライン装飾のマーカーも取り除いたうえで、改行区切りを保持したままの本文を返す。
 * サムネイルの先頭 24 文字プレビューなど、整形せずに「画面に見える内容」だけを見せたい箇所で
 * 使う（画面表示と同じく `[label](url)` は `label`、`**重要**` は `重要` になる）。
 * 水平線は画面に文字が出ないため空文字列、番号付きリストは画面と同じく「項番 + 本文」にする。
 * 表の各行は「セルの表示テキストを半角スペースで連結した 1 行」にし、区切り行は空文字列にする。
 */
export function stripMarkdownMarkersForDisplay(text: string): string {
  const sourceMap = buildMarkdownSourceMap(text);
  const displayLines: string[] = [];
  for (const block of sourceMap.blocks) {
    if (block.kind === "table") {
      const { table } = block;
      displayLines.push(tableRowDisplayText(text, table.header));
      displayLines.push("");
      for (const row of table.body) displayLines.push(tableRowDisplayText(text, row));
      continue;
    }
    if (block.kind === "code") {
      // フェンス行は画面に出ないため空文字列、中身の行は記法を解釈せず生テキストのまま。
      displayLines.push("");
      for (const lineIndex of block.code.contentLineIndexes) {
        displayLines.push(markdownRawLineText(sourceMap, lineIndex));
      }
      if (block.code.fenceEndLineIndex !== undefined) displayLines.push("");
      continue;
    }
    if (block.kind === "callout") {
      const { callout } = block;
      // フェンス行は空文字列にせず、出力から取り除く。本関数は各行を改行で連結するため、
      // 空文字列にすると先頭 24 文字のプレビューが空行から始まって中身がほとんど見えなくなる。
      if (markdownCalloutHasTitle(text, callout)) {
        displayLines.push(markdownCalloutTitleDisplayText(text, callout));
      }
      for (const body of callout.body) {
        displayLines.push(lineDisplayTextOf(sourceMap, body.lineIndex));
      }
      continue;
    }
    displayLines.push(lineDisplayTextOf(sourceMap, block.lineIndex));
  }
  return displayLines.join("\n");
}

//------------------------------------------------------------------------------
// 表の表示モデル（DOM 表示・計測ビルダーが共有する）
//------------------------------------------------------------------------------

/** 表の描画に必要な値をまとめたもの。列幅は共有の純粋関数で決めた em 値。 */
export interface MarkdownTableViewModel {
  /** 各列の幅（em）。`colgroup` の `width` へそのまま与える。 */
  readonly columnWidthsEm: readonly number[];
  /** 列幅の合計（em）。表自身の `width` へ明示する（`100%` にすると余剰幅が再配分されるため）。 */
  readonly totalWidthEm: number;
  readonly aligns: readonly MarkdownTableAlign[];
  readonly header: MarkdownTableRow;
  readonly body: readonly MarkdownTableRow[];
}

/**
 * ブロックごとの「直前の行の種別」を、ブロック配列と同じ並びで返す。
 * 連続するリスト行の項目間隔（`markdownLineStyleFor` の `previousKind`）を、非編集表示・コメント本文・
 * 計測ビルダーが同じ規則で決めるための共有関数。表はリスト行ではないため、表の直後は `undefined`
 * （＝項目間隔を付けない）になる。
 */
export function markdownBlockPreviousKinds(
  sourceMap: MarkdownSourceMap
): readonly (MarkdownTextLine["kind"] | undefined)[] {
  const previousKinds: (MarkdownTextLine["kind"] | undefined)[] = [];
  let previousKind: MarkdownTextLine["kind"] | undefined;
  for (const block of sourceMap.blocks) {
    previousKinds.push(previousKind);
    if (block.kind !== "line") {
      // 表・コードブロック・callout はリスト行ではないため、直後のリスト行に項目間隔を付けない。
      previousKind = undefined;
      continue;
    }
    previousKind = markdownTextLineFromParts(sourceMap.lines[block.lineIndex]!).kind;
  }
  return previousKinds;
}

/**
 * callout の本文行ごとの「直前の行の種別」を、`callout.body` と同じ並びで返す。
 * callout の中でも連続するリスト行へ項目間隔が付くようにする共有関数で、非編集表示・
 * コメント本文・計測ビルダー・Canvas 出力が同じ規則を使う。
 * 先頭の本文行は「直前の行が無い」扱いにする（タイトルはリスト行ではないため）。
 */
export function markdownCalloutBodyPreviousKinds(
  sourceMap: MarkdownSourceMap,
  callout: MarkdownCallout
): readonly (MarkdownTextLine["kind"] | undefined)[] {
  const previousKinds: (MarkdownTextLine["kind"] | undefined)[] = [];
  let previousKind: MarkdownTextLine["kind"] | undefined;
  for (const body of callout.body) {
    previousKinds.push(previousKind);
    previousKind = markdownTextLineFromParts(sourceMap.lines[body.lineIndex]!).kind;
  }
  return previousKinds;
}

/** 表の表示モデルへ、自動列幅の代わりに使う列幅を渡すための選択肢。 */
export interface MarkdownTableViewModelOptions {
  /** 保存済み列幅。列数とヘッダ指紋が一致するときだけ採用される（共有関数が判定する）。 */
  readonly savedWidths?: TableColumnWidths | null | undefined;
  /**
   * ドラッグ中のプレビュー列幅。保存値・自動列幅より優先する。
   * 列数が解析結果と食い違う場合（本文が編集された直後など）は無視する。
   */
  readonly previewWidthsEm?: readonly number[] | undefined;
}

/**
 * 表の表示モデルを求める。`availableEm` は表の使用可能幅（em）で、DOM の実測ではなく
 * 呼び出し側が本文領域の幅と基準フォント px から求めて渡す（`markdownTableAvailableEm`）。
 */
export function markdownTableViewModel(
  text: string,
  table: MarkdownTable,
  availableEm: number,
  options?: MarkdownTableViewModelOptions
): MarkdownTableViewModel {
  const preview = options?.previewWidthsEm;
  const columnWidthsEm =
    preview !== undefined && preview.length === table.columnCount
      ? preview
      : markdownTableColumnWidthsEm(text, table, availableEm, options?.savedWidths);
  return {
    aligns: table.aligns,
    body: table.body,
    columnWidthsEm,
    header: table.header,
    // 表自身の幅は列幅の合計。`width: 100%` にすると余剰幅が再配分され、colgroup の指定とずれる。
    totalWidthEm: roundEm(columnWidthsEm.reduce((sum, width) => sum + width, 0)),
  };
}

/**
 * セルの表示 span 列（部分書式 + 記法の装飾 + リンク）。行全体と同じ共有関数を raw 範囲指定で呼ぶため、
 * セルの中でもリンク・装飾・部分書式が従来どおり働く。`\|` はセル専用に `|` へ畳む。
 */
export function markdownTableCellSpans(
  text: string,
  cell: MarkdownTableCell,
  runs: readonly TextInlineStyleRange[] | undefined
): readonly TextInlineDisplaySpan[] {
  return buildTextInlineDisplaySpansForRange({
    collapsePipeEscape: true,
    end: cell.end,
    runs,
    start: cell.start,
    text,
  });
}

/**
 * 表図形（`type: "table"`）のセル本文の表示 span 列。画面・PDF・サムネイルの 3 経路が
 * この 1 つの関数から span を得ることで、同じセルの見た目が場所によってずれないようにする
 * （サムネイルは帯で描くため、`tableObjectCellDisplayText` 経由で表示テキストだけを使う）。
 *
 * - セル本文は生テキストのまま保存し、表示のときだけ記法を解釈する（編集中の入力欄は生テキスト）。
 * - 部分書式（run）は `runs` で渡す。記法由来の装飾との重ね方は、テキスト図形の本文と同じ規則を
 *   共有関数が決める（表のために新しい重ね方を作らない）。
 * - `collapsePipeEscape` は false。表図形のセルはパイプ区切りの中に無く、畳むと利用者が
 *   入力した `\|` の 2 文字が勝手に 1 文字へ変わってしまう（Markdown 表のセルとの違い）。
 */
export function tableObjectCellSpans(
  cell: string,
  runs?: readonly TextInlineStyleRange[] | undefined
): readonly TextInlineDisplaySpan[] {
  return buildTextInlineDisplaySpansForRange({
    collapsePipeEscape: false,
    end: cell.length,
    runs,
    start: 0,
    text: cell,
  });
}

/**
 * 表図形のセル本文の表示テキスト（記法の文字を取り除いた、画面に見える文字列）。
 * 文字ではなく帯で描くサムネイルが、帯の幅を画面と同じ文字列で測るために使う。
 * 部分書式は表示テキストを変えないため、ここでは受け取らない。
 */
export function tableObjectCellDisplayText(cell: string): string {
  return tableObjectCellSpans(cell)
    .map((span) => span.text)
    .join("");
}

/**
 * 表図形のセル本文の、生テキスト上のオフセット → 表示テキスト上のオフセットの写像。
 * 編集中の入力欄（生テキスト）で測った位置を、非編集表示のセル（記法を解釈した表示テキスト）
 * の中で使うために必要になる。リモートキャレットの描画が使う。
 * 記法の解釈条件は `tableObjectCellSpans` と同じにしなければならないため、同じ場所に置く。
 */
export function tableObjectCellDisplayOffset(cell: string, rawOffset: number): number {
  return displayOffsetInRange(cell, 0, cell.length, rawOffset, { collapsePipeEscape: false });
}

//------------------------------------------------------------------------------
// コードブロックの表示モデル（DOM 表示・コメント本文・計測ビルダーが共有する）
//------------------------------------------------------------------------------

/** コードブロックの 1 表示行（中身の行、または中身 0 行のブロックが確保する空行）。 */
export interface MarkdownCodeBlockLine {
  /** `data-md-line` へ与える raw 行 index（中身 0 行のブロックでは開始フェンス行の index）。 */
  readonly lineIndex: number;
  /**
   * 行の生テキストを部分書式（run）境界で分割した片。
   * Markdown の解釈（リンク化・インライン装飾・行マーカーの除去）は一切通さない。
   * 中身 0 行のブロックでは空配列。
   */
  readonly pieces: readonly TextInlineRawPiece[];
  /** 行の表示テキスト（`pieces` の連結）。空文字列の行は `<br>` で 1 行分の高さを確保する。 */
  readonly text: string;
}

/**
 * コードブロックの表示行を求める。中身の行は**元テキストから行の raw 範囲を切り出した文字列**を
 * 使う（行分類が `bullet` などになってもマーカーを落とさない）。部分書式（run）は
 * 既存の共有関数（`splitRawRangeByRuns`）で raw のまま区切るため、表示セグメント生成を
 * 通さずに run の色・サイズだけを反映できる。
 * 中身が 0 行のブロックは、開始フェンス行の index を持つ空行 1 件を返す（DOM は `<br>` だけの
 * span になり、面と枠が 1 行分の高さで出る）。
 *
 * なお、`- item` のように記法プレフィックスを持つコード行では、そのマーカー部分に run は当たらない
 * （範囲書式は `clipRangeToMarkdownVisible` で可視本文へ切り詰められ、編集中もマーカーは
 * `data-prefix` に隠れて選択できない）。マーカーに run があれば表示はするが、当てる経路は無い。
 */
export function markdownCodeBlockLines(
  map: MarkdownSourceMap,
  code: MarkdownCodeBlock,
  runs: readonly TextInlineStyleRange[] | undefined
): readonly MarkdownCodeBlockLine[] {
  if (code.contentLineIndexes.length === 0) {
    return [{ lineIndex: code.fenceStartLineIndex, pieces: [], text: "" }];
  }
  // run が重ならない行では分割そのものが不要。splitRawRangeByRuns は呼び出しごとに本文全長の
  // override マップを確保するため、行数ぶん無条件に呼ぶと本文長 × 行数に比例した割り当てが起きる
  // （付箋のフォント自動調整は同じレイアウトを二分探索で何度も走らせる）。run の重なり判定は
  // 正規形の run 配列（最大 256 件）の走査だけで済む。
  return code.contentLineIndexes.map((lineIndex) => {
    const rawStart = map.lines[lineIndex]?.rawStart ?? 0;
    const text = markdownRawLineText(map, lineIndex);
    const rawEnd = rawStart + text.length;
    const overlapped =
      runs !== undefined && runs.some((run) => run.start < rawEnd && run.end > rawStart);
    return {
      lineIndex,
      pieces: overlapped
        ? splitRawRangeByRuns({ rawEnd, rawStart, runs, text: map.text })
        : [{ rawEnd, rawStart, style: {}, text }],
      text,
    };
  });
}

/**
 * コード行の片に当てる CSS。上書きが 1 項目も無ければ undefined を返し、呼び出し側は
 * span で包まない素のテキストノードにする（非編集表示・コメント本文・計測ビルダーで
 * まったく同じ DOM 構造にするため、判定をここへ集約する）。
 * 記法の装飾はコードブロックの中では成立しないため、`decoration` は渡さない。
 */
export function markdownCodeSpanCss(style: TextInlineStyle): TextInlineStyleCss | undefined {
  const css = textInlineStyleCss(style);
  return Object.keys(css).length > 0 ? css : undefined;
}

/** markdownTextLineFromParts へ渡す、1 行だけでは決まらない情報。 */
export interface MarkdownTextLineOptions {
  /**
   * 番号付きリスト行の採番済み接頭辞（`MarkdownSourceMap.orderedNumbers` の値）。
   * 省略時は defaultOrderedLabelFor が行単体で組み立てる（採番の後段を通していない呼び出しでも
   * 壊れないための保険で、通常は使われない）。
   */
  readonly orderedLabel?: string | undefined;
}

/**
 * MarkdownLineParts（domain の行分類）を表示モデル（MarkdownTextLine）へ変換する。
 * 編集 DOM（markdown-wysiwyg.ts）が行スタイルを求める際にも使う。
 * `sourceMap.lines` を直接渡す呼び出し元（非編集表示・編集 DOM・部分書式ありの Canvas）は、
 * `sourceMap.orderedNumbers.get(line.lineIndex)` を `orderedLabel` として渡すこと。
 */
export function markdownTextLineFromParts(
  parts: MarkdownLineParts,
  options?: MarkdownTextLineOptions | undefined
): MarkdownTextLine {
  switch (parts.kind) {
    case "blank":
      return { kind: "blank" };
    case "heading": {
      const level = markdownHeadingLevel(parts.level);
      // parts.kind==="heading" は level 1〜6 を保証するが、型を HeadingLevel へ絞るため再確認する。
      if (level === undefined) return { content: parts.content, kind: "plain" };
      return { content: parts.content, kind: "heading", level };
    }
    case "bullet":
      return { content: parts.content, kind: "bullet", level: parts.level };
    case "task":
      return {
        checked: parts.checked,
        content: parts.content,
        kind: "task",
        level: parts.level,
      };
    case "quote":
      return { content: parts.content, kind: "quote" };
    case "ordered":
      return {
        content: parts.content,
        kind: "ordered",
        label: options?.orderedLabel ?? defaultOrderedLabelFor(parts),
        level: parts.level,
      };
    case "rule":
      return { content: parts.content, kind: "rule" };
    case "plain":
      return { content: parts.content, kind: "plain" };
    default:
      return assertNever(parts.kind);
  }
}

//------------------------------------------------------------------------------
// Helpers
//------------------------------------------------------------------------------

/**
 * 採番結果（`orderedLabel`）を受け取れなかったときの既定の接頭辞。
 * 項番を割り当てない第 3 階層以降では箇条書きマーカーを返し、assignOrderedListNumbers と
 * 同じ規則にそろえる（規則が 2 か所で食い違うと、この経路だけ枝番が出てしまう）。
 * 浅い階層は他行を見ないと採番できないため、従来どおり行の生テキストの数字を使う。
 */
function defaultOrderedLabelFor(parts: MarkdownLineParts): string {
  if (parts.level > ORDERED_LABEL_MAX_LEVEL) return bulletMarkerFor(parts.level);
  return `${markdownOrderedSourceNumber(parts) ?? 1}.`;
}

/** 計測用ビルダーの行 1 つぶん（`div[data-md-line]`）を積む。callout の本文行も同じ関数を通す。 */
function appendMeasuredLine(
  ownerDocument: Document,
  parent: HTMLElement,
  text: string,
  sourceMap: MarkdownSourceMap,
  lineIndex: number,
  previousKind: MarkdownTextLine["kind"] | undefined
): void {
  const sourceLine = sourceMap.lines[lineIndex]!;
  const line = markdownTextLineFromParts(sourceLine, {
    orderedLabel: sourceMap.orderedNumbers.get(sourceLine.lineIndex),
  });
  const lineElement = ownerDocument.createElement("div");
  // 行要素にも raw 行 index を付け、表示側（MarkdownTextView）と同じ属性構造にする。
  lineElement.dataset.mdLine = String(sourceLine.lineIndex);
  const style = markdownLineStyleFor(line, { previousKind });
  if (style.fontSize !== undefined) lineElement.style.fontSize = style.fontSize;
  if (style.fontWeight !== undefined) lineElement.style.fontWeight = style.fontWeight;
  if (style.marginTop !== undefined) lineElement.style.marginTop = style.marginTop;
  if (style.paddingLeft !== undefined) lineElement.style.paddingLeft = style.paddingLeft;
  if (style.textAlign !== undefined) lineElement.style.textAlign = style.textAlign;
  if (style.textIndent !== undefined) lineElement.style.textIndent = style.textIndent;
  // 引用・水平線の見た目は CSS クラスで与える（表示側と同じ関数の結果をそのまま使う）。
  const className = markdownLineClassName(line);
  if (className !== undefined) lineElement.className = className;
  appendMeasuredLineContent(ownerDocument, lineElement, line, sourceLine, text);
  parent.appendChild(lineElement);
}

/** プレビュー縮約の 1 行ぶん（水平線・空行は空文字列、番号付きリストは項番付き）。 */
function lineDisplayTextOf(sourceMap: MarkdownSourceMap, lineIndex: number): string {
  const sourceLine = sourceMap.lines[lineIndex]!;
  const line = markdownTextLineFromParts(sourceLine, {
    orderedLabel: sourceMap.orderedNumbers.get(sourceLine.lineIndex),
  });
  switch (line.kind) {
    case "blank":
    case "rule":
      return "";
    case "ordered":
      return markdownLineDisplayTextFor(line);
    default:
      return markdownContentDisplayText(line.content);
  }
}

/** 計測用ビルダーの 1 行ぶんの中身（単一テキストノード / 装飾 span 列 / 空行の `<br>`）を積む。 */
function appendMeasuredLineContent(
  ownerDocument: Document,
  lineElement: HTMLElement,
  line: MarkdownTextLine,
  sourceLine: MarkdownSourceLine,
  text: string
): void {
  const spans = buildTextInlineDisplaySpans({ line: sourceLine, runs: undefined, text });
  const lineText = markdownLineDisplayTextFor(line);
  if (lineText.length === 0 && line.kind !== "task") {
    // 空行は <br> で 1 行分の高さを維持する（空 div は高さ 0 になるため）。
    // 空白のみの行は plain として textContent が入り、pre-wrap コンテナ側で高さが出る。
    // 水平線（表示テキストは空。横罫は CSS の背景）と `>` だけの引用も同じ構造にし、
    // 表示側（MarkdownTextView）との構造パリティを保つ。
    lineElement.appendChild(ownerDocument.createElement("br"));
    return;
  }
  if (line.kind === "task") {
    lineElement.appendChild(createTaskBoxElement(ownerDocument, line.checked));
  }
  // 箇条書きのグリフ（`•`）と番号付きリストの項番（`1.`）は装飾を持たない表示専用の接頭辞。
  // 非編集表示とまったく同じ固定幅の span として本文の手前へ置く。素のテキストのまま置くと
  // 占有幅がグリフの実描画幅になり、付箋のフォント自動調整が実表示と違う折り返しで収まりを判定する。
  const markerText = markdownListMarkerTextFor(line);
  if (markerText.length > 0) {
    lineElement.appendChild(createListMarkerElement(ownerDocument, line, markerText));
  }
  const decorated = spans.some(
    (span) => !isSameMarkdownInlineDecoration(span.decoration, NO_MARKDOWN_INLINE_DECORATION)
  );
  if (!decorated) {
    const bodyText = markdownLineBodyDisplayTextFor(line);
    // マーカーだけの行（本文が空の箇条書き）では、空のテキストノードを置かない。
    if (bodyText.length > 0) lineElement.appendChild(ownerDocument.createTextNode(bodyText));
    return;
  }
  for (const span of spans) {
    lineElement.appendChild(
      createDecoratedSpanElement(ownerDocument, span, line.kind === "heading")
    );
  }
}

/**
 * 計測用ビルダーの表 1 つぶんを積む。表示側（MarkdownTableView）と同じ
 * wrapper / table（width = 列幅合計）/ colgroup / thead / tbody の構造と、セルの
 * `data-md-line` / `data-md-cell` / `text-align` を持つ。
 */
function appendMeasuredTable(
  ownerDocument: Document,
  element: HTMLElement,
  text: string,
  table: MarkdownTable,
  availableEm: number,
  options?: MarkdownTableViewModelOptions
): void {
  const model = markdownTableViewModel(text, table, availableEm, options);
  const wrap = ownerDocument.createElement("div");
  wrap.className = MARKDOWN_TABLE_WRAP_CLASS;
  const tableElement = ownerDocument.createElement("table");
  tableElement.className = MARKDOWN_TABLE_CLASS;
  tableElement.style.width = `${model.totalWidthEm}em`;
  const colgroup = ownerDocument.createElement("colgroup");
  for (const widthEm of model.columnWidthsEm) {
    const col = ownerDocument.createElement("col");
    col.style.width = `${widthEm}em`;
    colgroup.appendChild(col);
  }
  tableElement.appendChild(colgroup);
  const thead = ownerDocument.createElement("thead");
  thead.appendChild(createMeasuredTableRow(ownerDocument, text, model, model.header, "th"));
  tableElement.appendChild(thead);
  if (model.body.length > 0) {
    const tbody = ownerDocument.createElement("tbody");
    for (const row of model.body) {
      tbody.appendChild(createMeasuredTableRow(ownerDocument, text, model, row, "td"));
    }
    tableElement.appendChild(tbody);
  }
  wrap.appendChild(tableElement);
  element.appendChild(wrap);
}

/** 計測用ビルダーの表の 1 行（`<tr>`）。セル要素だけが `data-md-line` / `data-md-cell` を持つ。 */
function createMeasuredTableRow(
  ownerDocument: Document,
  text: string,
  model: MarkdownTableViewModel,
  row: MarkdownTableRow,
  cellTag: "th" | "td"
): HTMLTableRowElement {
  const tr = ownerDocument.createElement("tr");
  for (const [cellIndex, cell] of row.cells.entries()) {
    const cellElement = ownerDocument.createElement(cellTag);
    cellElement.dataset.mdLine = String(row.lineIndex);
    cellElement.dataset.mdCell = String(cellIndex);
    // 列ヘッダーは表示側と同じく `scope="col"` を持つ（支援技術向けの列見出し）。
    if (cellTag === "th") cellElement.setAttribute("scope", "col");
    cellElement.style.textAlign = model.aligns[cellIndex] ?? "left";
    const spans = markdownTableCellSpans(text, cell, undefined);
    const decorated = spans.some(
      (span) => !isSameMarkdownInlineDecoration(span.decoration, NO_MARKDOWN_INLINE_DECORATION)
    );
    if (!decorated) {
      // 装飾を含まないセルは単一のテキストノード（行の計測と同じ方針）。空セルは子を持たない。
      cellElement.textContent = spans.map((span) => span.text).join("");
    } else {
      for (const span of spans) {
        cellElement.appendChild(createDecoratedSpanElement(ownerDocument, span, false));
      }
    }
    tr.appendChild(cellElement);
  }
  return tr;
}

/**
 * 計測用ビルダーのコードブロック 1 つぶんを積む。表示側（MarkdownCodeBlockView）と同じ
 * `pre.canvas-md-code > code > span[data-md-line]` の構造を作る。
 * `pre` と `code` には `data-md-line` を付けない（付けると行要素を引くセレクタが `pre` 全体を選び、
 * 範囲選択の矩形収集で `pre` と各行 span が二重に集まる）。
 */
function appendMeasuredCodeBlock(
  ownerDocument: Document,
  element: HTMLElement,
  sourceMap: MarkdownSourceMap,
  code: MarkdownCodeBlock
): void {
  const pre = ownerDocument.createElement("pre");
  pre.className = MARKDOWN_CODE_CLASS;
  const codeElement = ownerDocument.createElement("code");
  // 計測ビルダーは部分書式を持たない（表示側の run 無しと同じ構造になる）。
  for (const line of markdownCodeBlockLines(sourceMap, code, undefined)) {
    const lineElement = ownerDocument.createElement("span");
    lineElement.dataset.mdLine = String(line.lineIndex);
    if (line.text.length === 0) {
      // 空の span は高さ 0 になるため、空行・空ブロックは <br> で 1 行分を確保する。
      lineElement.appendChild(ownerDocument.createElement("br"));
    } else {
      for (const piece of line.pieces) {
        const css = markdownCodeSpanCss(piece.style);
        if (css === undefined) {
          lineElement.appendChild(ownerDocument.createTextNode(piece.text));
          continue;
        }
        const pieceElement = ownerDocument.createElement("span");
        applyTextInlineStyleCss(pieceElement, css);
        pieceElement.textContent = piece.text;
        lineElement.appendChild(pieceElement);
      }
    }
    codeElement.appendChild(lineElement);
  }
  pre.appendChild(codeElement);
  element.appendChild(pre);
}

/**
 * 計測用ビルダーの callout 1 つぶんを積む。表示側（MarkdownCalloutView）と同じ
 * `div.canvas-md-callout > (タイトル / 本文行 / 空行)` の構造を作る。
 * コンテナへ `data-md-line` を付けない（付けると行要素を引くセレクタがコンテナを選び、
 * 範囲選択の矩形収集でコンテナと各行が二重に集まる）。
 */
function appendMeasuredCallout(
  ownerDocument: Document,
  element: HTMLElement,
  text: string,
  sourceMap: MarkdownSourceMap,
  callout: MarkdownCallout
): void {
  const container = ownerDocument.createElement("div");
  container.className = markdownCalloutClassName(callout.type);
  const hasTitle = markdownCalloutHasTitle(text, callout);
  if (hasTitle) {
    const titleElement = ownerDocument.createElement("div");
    titleElement.className = MARKDOWN_CALLOUT_TITLE_CLASS;
    // タイトル要素の raw 行 index は開始フェンス行。この callout でキャレットを表現できる
    // 唯一の要素になることがあるため、必ず付ける。
    titleElement.dataset.mdLine = String(callout.openLineIndex);
    const spans = markdownCalloutTitleSpans(text, callout, undefined);
    const decorated = spans.some(
      (span) => !isSameMarkdownInlineDecoration(span.decoration, NO_MARKDOWN_INLINE_DECORATION)
    );
    if (decorated) {
      // 装飾を含むタイトルは表示側とまったく同じ解決で span を作る（行の計測と同じ方針）。
      for (const span of spans) {
        titleElement.appendChild(createDecoratedSpanElement(ownerDocument, span, false));
      }
    } else {
      titleElement.textContent = markdownCalloutTitleDisplayText(text, callout);
    }
    container.appendChild(titleElement);
  }
  const previousKinds = markdownCalloutBodyPreviousKinds(sourceMap, callout);
  for (const [index, body] of callout.body.entries()) {
    appendMeasuredLine(
      ownerDocument,
      container,
      text,
      sourceMap,
      body.lineIndex,
      previousKinds[index]
    );
  }
  if (!hasTitle && callout.body.length === 0) {
    // 本文 0 行・タイトル無しの callout は <br> だけの行で 1 行分の高さを確保する
    // （空の div は高さ 0 になる）。data-md-line を付けないと、この callout の中に
    // キャレットを表現できる要素が 1 つも無くなる。
    const emptyLine = ownerDocument.createElement("div");
    emptyLine.dataset.mdLine = String(callout.openLineIndex);
    emptyLine.appendChild(ownerDocument.createElement("br"));
    container.appendChild(emptyLine);
  }
  element.appendChild(container);
}

/**
 * リスト行のマーカー span を作る。非編集表示（MarkdownLineView）とまったく同じクラスと
 * `--marker-width` を与え、計測（付箋のフォント自動調整）と実表示の占有幅をそろえる。
 */
function createListMarkerElement(
  ownerDocument: Document,
  line: MarkdownTextLine,
  markerText: string
): HTMLElement {
  const marker = ownerDocument.createElement("span");
  const className = markdownListMarkerClassName(line);
  if (className !== undefined) marker.className = className;
  const style = markdownListMarkerStyleFor(line);
  if (style !== undefined) {
    marker.style.setProperty(
      MARKDOWN_LIST_MARKER_WIDTH_PROPERTY,
      style[MARKDOWN_LIST_MARKER_WIDTH_PROPERTY]
    );
  }
  marker.textContent = markerText;
  return marker;
}

/** 計測 DOM 用の静的なチェックボックス。表示 DOM と同じ外寸・状態属性を持つ。 */
function createTaskBoxElement(ownerDocument: Document, checked: boolean): HTMLElement {
  const box = ownerDocument.createElement("span");
  box.className = MARKDOWN_TASK_BOX_CLASS;
  box.dataset.checked = String(checked);
  return box;
}

/** 解決済みの CSS 値を span の style へ写す（表示側の `style` 属性と同じ項目・同じ順序）。 */
function applyTextInlineStyleCss(element: HTMLElement, css: TextInlineStyleCss): void {
  if (css.color !== undefined) element.style.color = css.color;
  if (css.backgroundColor !== undefined) element.style.backgroundColor = css.backgroundColor;
  if (css.fontSize !== undefined) element.style.fontSize = css.fontSize;
  if (css.fontWeight !== undefined) element.style.fontWeight = css.fontWeight;
  if (css.fontStyle !== undefined) element.style.fontStyle = css.fontStyle;
  if (css.textDecorationLine !== undefined) {
    element.style.textDecorationLine = css.textDecorationLine;
  }
}

/** 表の 1 行のプレビュー縮約（セルの表示テキストを半角スペースで連結した 1 行）。 */
function tableRowDisplayText(text: string, row: MarkdownTableRow): string {
  return row.cells.map((cell) => markdownTableCellDisplayText(text, cell)).join(" ");
}

/** 表示側（renderInlineDecoratedSpans）とまったく同じ解決で計測用の span を作る。 */
function createDecoratedSpanElement(
  ownerDocument: Document,
  span: TextInlineDisplaySpan,
  headingLine: boolean
): HTMLElement {
  const element = ownerDocument.createElement("span");
  const className = textInlineDecorationClassName(span.decoration, {
    codeEnd: span.codeEnd,
    codeStart: span.codeStart,
  });
  if (className !== undefined) element.className = className;
  applyTextInlineStyleCss(
    element,
    textInlineStyleCss(span.style, { decoration: span.decoration, headingLine })
  );
  element.textContent = span.text;
  return element;
}

/**
 * 行の可視本文（記法プレフィックスを除いた content）から「画面に見える文字列」を返す。
 * 表示テキスト付きリンクは表示テキストへ縮約し、インライン装飾のマーカーは取り除く
 * （素の URL はそのまま残る）。判定は domain の表示セグメントを単一の真実として共有する。
 */
function markdownContentDisplayText(content: string): string {
  return markdownLineDisplaySegments(displaySourceLineOf(content))
    .map((segment) => segment.text)
    .join("");
}

/**
 * 可視本文だけを持つ合成の MarkdownSourceLine を作る。
 * 表示セグメントの算出は content と contentStart しか見ないため、raw offset を 0 起点にした
 * この合成行で「1 行ぶんの表示テキスト」を求められる。
 */
function displaySourceLineOf(content: string): MarkdownSourceLine {
  return {
    checked: false,
    content,
    contentEnd: content.length,
    contentStart: 0,
    glyph: "",
    kind: "plain",
    level: 0,
    lineIndex: 0,
    rawPrefix: "",
    rawStart: 0,
  };
}

// 浮動小数の掛け算誤差（例: 3 × 1.2 = 3.5999…）が CSS 値へ漏れないよう丸める。
function roundEm(value: number): number {
  return Math.round(value * 1000) / 1000;
}

function assertNever(value: never): never {
  throw new Error(`unexpected markdown line: ${JSON.stringify(value)}`);
}
